/**
 * Updates the questionnaire flow based on answers provided by the user
 *
 * @param {Array} questions - The processed questions array with children mapping
 * @param {Object} answers - The form values/answers keyed by field IDs (fid)
 * @returns {Array} - Filtered questions array based on conditional logic
 */
export const updateQuestionnaireFlow = (
  questions: string | any[],
  answers: { [x: string]: any }
) => {
  if (!questions || !Array.isArray(questions) || questions.length === 0) {
    return [];
  }

  // Create a deep clone to avoid modifying the original question array
  const processedQuestions = JSON.parse(JSON.stringify(questions));

  // Set to track valid question IDs based on answers
  const validQuestionIds = new Set();

  // Always include the first question
  if (processedQuestions[0] && processedQuestions[0].id) {
    validQuestionIds.add(processedQuestions[0].id.toString());
  }

  // Create a map for easier question lookup by ID
  const questionsMap: any = {};
  processedQuestions.forEach((question: any) => {
    if (question.id) {
      questionsMap[question.id.toString()] = question;
    }
  });

  // Process the questions to determine which ones should be shown
  for (let i = 0; i < processedQuestions.length; i++) {
    const question = processedQuestions[i];
    if (!question || !question.id) continue;

    const questionId = question.id.toString();

    // Skip questions that aren't valid based on conditional logic
    if (!validQuestionIds.has(questionId)) continue;

    // Handle questions with conditional options that could lead to different paths
    if (
      (question.control === 'dropdown' || question.control === 'radio') &&
      question.conditional
    ) {
      const answer = answers[question.fid];

      // If there's an answer and this question has options
      if (answer && question.options && Array.isArray(question.options)) {
        // Find the selected option
        const selectedOption = question.options.find(
          (opt: { value: any }) => opt.value === answer
        );

        if (selectedOption && selectedOption.target) {
          // Add the target to valid IDs
          validQuestionIds.add(selectedOption.target.toString());

          // Check if the target is a screen - if so, don't add any children
          const targetQuestion = questionsMap[selectedOption.target.toString()];
          if (
            targetQuestion &&
            targetQuestion.control !== 'screen' &&
            targetQuestion.isScreen !== true
          ) {
            // Only add children if the target is not a screen
            if (
              targetQuestion.children &&
              Array.isArray(targetQuestion.children)
            ) {
              targetQuestion.children.forEach(
                (childId: { toString: () => unknown }) => {
                  if (childId) validQuestionIds.add(childId.toString());
                }
              );
            }
          }

          // Mark other paths as invalid
          if (question.children && Array.isArray(question.children)) {
            question.children.forEach((childId: { toString: () => any }) => {
              if (
                childId &&
                childId.toString() !== selectedOption.target.toString()
              ) {
                // Remove this child and all its descendants
                removeDescendantQuestions(
                  childId.toString(),
                  questionsMap,
                  validQuestionIds
                );
              }
            });
          }
          // Recheck if current selected target was removed
          if (!validQuestionIds.has(selectedOption.target.toString())) {
            validQuestionIds.add(selectedOption.target.toString());
          }
        } else {
          // If no target for this answer, add all direct children to valid IDs
          if (question.children && Array.isArray(question.children)) {
            question.children.forEach(
              (childId: { toString: () => unknown }) => {
                if (childId) validQuestionIds.add(childId.toString());
              }
            );
          }
        }
      } else {
        // If no answer yet, keep all possible paths open
        if (question.children && Array.isArray(question.children)) {
          question.children.forEach((childId: { toString: () => unknown }) => {
            if (childId) validQuestionIds.add(childId.toString());
          });
        }
      }
    } else {
      // For questions with generalTarget, prioritize that path
      if (question.generalTarget && question.generalTarget.id) {
        // Use generalTarget to determine the next path
        validQuestionIds.add(question.generalTarget.id.toString());

        // Check if the target is a screen
        const targetQuestion =
          questionsMap[question.generalTarget.id.toString()];
        if (
          targetQuestion &&
          targetQuestion.control !== 'screen' &&
          targetQuestion.isScreen !== true
        ) {
          // Add children of the target question
          if (
            targetQuestion.children &&
            Array.isArray(targetQuestion.children)
          ) {
            targetQuestion.children.forEach(
              (childId: { toString: () => unknown }) => {
                if (childId) validQuestionIds.add(childId.toString());
              }
            );
          }
        }

        // If this question has other children, remove them as we're prioritizing generalTarget
        if (question.children && Array.isArray(question.children)) {
          question.children.forEach((childId: { toString: () => string }) => {
            if (
              childId &&
              childId.toString() !== question.generalTarget.id.toString()
            ) {
              removeDescendantQuestions(
                childId.toString(),
                questionsMap,
                validQuestionIds
              );
            }
          });
          // Recheck if current selected target was removed
          if (!validQuestionIds.has(question.generalTarget.id.toString())) {
            validQuestionIds.add(question.generalTarget.id.toString());
          }
        }
      } else {
        // For control types without conditional logic or generalTarget
        if (question.children && Array.isArray(question.children)) {
          question.children.forEach((childId: { toString: () => unknown }) => {
            if (childId) validQuestionIds.add(childId.toString());
          });
        }
      }
    }
  }

  const filteredQuestions = processedQuestions.filter(
    (question: { id: { toString: () => unknown } }) =>
      question && question.id && validQuestionIds.has(question.id.toString())
  );
  return filteredQuestions;
};

/**
 * Recursively removes a question and all its descendants from valid questions
 *
 * @param {string} questionId - ID of the question to remove
 * @param {Object} questionsMap - Map of questions by ID for lookup
 * @param {Set} validIds - Set of valid question IDs
 */
const removeDescendantQuestions = (
  questionId: string | number,
  questionsMap: { [x: string]: any },
  validIds: Set<unknown>
) => {
  if (!questionId) return;

  // Remove this question from valid IDs
  validIds.delete(questionId);

  // Get the question object
  const question = questionsMap[questionId];
  if (!question || !question.children || !Array.isArray(question.children))
    return;

  // Recursively remove all children
  question.children.forEach((childId: { toString: () => any }) => {
    if (childId) {
      removeDescendantQuestions(childId.toString(), questionsMap, validIds);
    }
  });
};

/**
 * Helper function to find the parent of a question
 *
 * @param {string} questionId - ID of the question to find parent for
 * @param {Array} questions - The full array of questions
 * @returns {Object|null} - The parent question or null if not found
 */
export const findParentQuestion = (questionId: any, questions: any[]) => {
  if (!questionId || !questions || !Array.isArray(questions)) return null;

  return questions.find(
    (question) =>
      question.children &&
      Array.isArray(question.children) &&
      question.children.includes(questionId)
  );
};

/**
 * Helper to determine if a question should be shown based on dependencies
 *
 * @param {Object} question - The question object
 * @param {Object} answers - The current form answers
 * @param {Array} allQuestions - All questions for reference
 * @returns {boolean} - Whether the question should be displayed
 */
export const shouldShowQuestion = (
  question: { conditional: any; id: any },
  answers: { [x: string]: any },
  allQuestions: any
) => {
  if (!question) return false;

  // Non-conditional questions are always shown
  if (!question.conditional) return true;

  // Find parent question(s)
  const parent = findParentQuestion(question.id, allQuestions);
  if (!parent) return true; // If no parent found, show by default

  // Check if parent answer directs to this question
  if (parent.control === 'dropdown' || parent.control === 'radio') {
    const parentAnswer = answers[parent.fid];

    // Find if this answer points to current question
    const matchingOption = parent.options?.find(
      (opt: { value: any; target: any }) =>
        opt.value === parentAnswer && opt.target === question.id
    );

    return !!matchingOption;
  } else if (parent.control === 'checkbox') {
    const parentAnswers = answers[parent.fid];

    if (!Array.isArray(parentAnswers) || parentAnswers.length === 0) {
      return false;
    }

    // Check if any selected checkbox option points to this question
    return parent.options?.some(
      (opt: { value: any; target: any }) =>
        parentAnswers.includes(opt.value) && opt.target === question.id
    );
  }

  return true;
};
